import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, IconButton, Paper, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import api from "../api/axios";
import { mandantUrl, useBusinessContext } from "../BusinessContext";
import UmsatzDialog from "../components/UmsatzDialog";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';


const money = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function Umsaetze({ modus: propModus }) {
  const { mandantId, betriebsjahrId, betriebsjahre, params, withContext, selectedMandant } = useBusinessContext();
  const generation = useRef(0);
  const transactionScope = useRef(null);
  const [searchParams] = useSearchParams();
  const modus = ["ein", "einnahme", "einnahmen"].includes((searchParams.get("modus") || propModus || "ausgaben").toLowerCase()) ? "einnahmen" : "ausgaben";
  const art = modus === "einnahmen" ? "Ein" : "Aus";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [kategorien, setKategorien] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false), [catalogError, setCatalogError] = useState(''), [catalogAttempt, setCatalogAttempt] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState({ text: "", severity: "success" });

  // Master data depends only on the tenant, independently of transaction filters.
  useEffect(() => {
    let active = true;
    setKategorien([]);
    setCatalogLoading(Boolean(mandantId)); setCatalogError('');
    setDialogOpen(false);
    setSelected(null);
    if (mandantId) {
      api.get(mandantUrl("/kategorien", mandantId)).then(response => {
        if (active) setKategorien(Array.isArray(response.data) ? response.data : []);
      }).catch(error => {
        if (active) setCatalogError(error.response?.data?.error || 'Kategorien konnten nicht geladen werden.');
      }).finally(() => { if (active) setCatalogLoading(false); });
    }
    return () => { active = false; };
  }, [mandantId, catalogAttempt]);

  const listUrl = useMemo(() => params ? withContext(`/umsaetze?art=${art.toLowerCase()}`) : null, [art, params, withContext]);
  const load = useCallback(async () => {
    const job = ++generation.current;
    setItems([]); setLoadError(''); setLoading(false);
    if (!mandantId || !listUrl) return;
    setLoading(true);
    try {
      const transactions = await api.get(listUrl);
      if (job !== generation.current) return;
      setItems(Array.isArray(transactions.data) ? transactions.data : []);
    } catch (error) {
      if (job !== generation.current) return;
      setItems([]);
      setLoadError(error.response?.data?.error || 'Umsätze konnten nicht geladen werden.');
    } finally { if (job === generation.current) setLoading(false); }
  }, [listUrl, mandantId]);

  useEffect(() => {
    const scope = { active: true };
    transactionScope.current = scope;
    setDialogOpen(false);
    setSelected(null);
    load();
    return () => { scope.active = false; generation.current += 1; };
  }, [load]);

  const openDialog = (item = null) => {
    setSelected(item);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const save = async (payload) => {
    const scope = transactionScope.current;
    if (!scope?.active || !params) return;
    const body = { ...payload, ...params, betriebsjahr_id: payload.betriebsjahr_id };
    if (selected) {
      const url = Number(payload.betriebsjahr_id) === Number(selected.betriebsjahr_id)
        ? withContext(`/umsaetze/${selected.id}`) : `/umsaetze/${selected.id}`;
      await api.put(url, body);
    } else await api.post("/umsaetze", body);
    if (!scope.active) return;
    closeDialog();
    await load();
    if (scope.active) setMessage({ text: `${art === "Ein" ? "Einnahme" : "Ausgabe"} erfolgreich gespeichert.`, severity: "success" });
  };

  const remove = async (item) => {
    const scope = transactionScope.current;
    if (!scope?.active || !params) return;
    if (!window.confirm(`Soll ${art === "Ein" ? "die Einnahme" : "die Ausgabe"} wirklich gelöscht werden?`)) return;
    try {
      await api.delete(withContext(`/umsaetze/${item.id}`));
      if (!scope.active) return;
      await load();
      if (scope.active) setMessage({ text: `${art === "Ein" ? "Einnahme" : "Ausgabe"} erfolgreich gelöscht.`, severity: "success" });
    } catch (error) {
      if (scope.active) setMessage({ text: error.response?.data?.error || "Der Umsatz konnte nicht gelöscht werden.", severity: "error" });
    }
  };

  return (
    <Box>
      <PageHeader
        title={modus === "einnahmen" ? "Einnahmen" : "Ausgaben"}
        action={<Button variant="contained" size="small" startIcon={<Add />} disabled={!params} onClick={() => openDialog()}>Hinzufügen</Button>}
      />
      {!params && <Alert severity="info">Bitte einen vollständigen Geschäftskontext auswählen.</Alert>}
      {loading && <LoadingIndicator label={modus === 'einnahmen' ? 'Einnahmen werden geladen …' : 'Ausgaben werden geladen …'} />}
      {catalogLoading && <LoadingIndicator label="Kategorien werden geladen …" compact />}
      {catalogError && <LoadError onRetry={() => setCatalogAttempt(value => value + 1)}>{catalogError}</LoadError>}
      {loadError && <LoadError onRetry={load}>{loadError}</LoadError>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Datum</TableCell><TableCell>Nr</TableCell><TableCell>Zweck</TableCell>
            <TableCell align="right">Betrag</TableCell><TableCell>Kategorie</TableCell><TableCell align="right" sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {!loading && !loadError && params && items.length === 0 ? <TableRow><TableCell colSpan={6} align="center">Keine Einträge.</TableCell></TableRow> : items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.datum ? dayjs(item.datum).format("DD.MM.YYYY") : ""}</TableCell>
                <TableCell>{item.nr ?? ""}</TableCell>
                <TableCell>{item.zweck}</TableCell>
                <TableCell align="right">{money.format(item.betrag)}</TableCell>
                <TableCell>{item.kategorie_kuerzel}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Bearbeiten"><IconButton size="small" color="primary" aria-label={`Umsatz ${item.id} bearbeiten`} onClick={() => openDialog(item)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Löschen"><IconButton size="small" color="error" aria-label={`Umsatz ${item.id} löschen`} onClick={() => remove(item)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UmsatzDialog open={dialogOpen} onClose={closeDialog} onSave={save} umsatz={selected} art={art} kategorien={kategorien} loading={catalogLoading} betriebsjahre={betriebsjahre} currentBetriebsjahr={betriebsjahrId} yearLabel={selectedMandant?.BetriebsjahrTitel} />
      <Snackbar
        open={Boolean(message.text)}
        autoHideDuration={4000}
        onClose={() => setMessage((current) => ({ ...current, text: "" }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={message.severity} variant="filled">{message.text}</Alert>
      </Snackbar>
    </Box>
  );
}
